import { api, unwrap } from "@lumberset/api-client";
import { queryOptions, useQueryClient } from "@tanstack/react-query";

/** Custom plan offers: the Admin's list, one workspace's offers, and the calls behind sending one. */

export const offerStatuses = ["pending", "accepted", "declined", "withdrawn", "expired"] as const;
export type OfferStatus = (typeof offerStatuses)[number];
export type OfferStatusFilter = OfferStatus | "all";

const fetchOffers = (query: { yardId?: string; status?: OfferStatusFilter }) =>
  unwrap(api.admin.offers.$get({ query }));

export type OffersResult = Awaited<ReturnType<typeof fetchOffers>>;
export type AdminOffer = OffersResult["offers"][number];
export type OfferWorkspace = NonNullable<OffersResult["workspace"]>;

export const offersQuery = (filters: { yardId?: string; status?: OfferStatusFilter } = {}) =>
  queryOptions({
    queryKey: ["admin", "offers", filters],
    queryFn: () => fetchOffers(filters),
  });

export interface OfferFields {
  yardId: string;
  name: string;
  monthlyFeeCents: number;
  includedQuotes: number;
  quoteFeeCents: number;
  changeOrderFeeCents: number;
  message: string | null;
  expiresAt: string | null;
  ticketId: string | null;
}

export const offerActions = {
  send: (json: OfferFields) => unwrap(api.admin.offers.$post({ json })),
  withdraw: (id: string) => unwrap(api.admin.offers[":id"].withdraw.$post({ param: { id } })),
  /** The hourly monthly renewal, run now. Charges only months that are due, never twice. */
  runRenewals: () => unwrap(api.admin.offers.renewals.$post()),
};

/** After a change: the offer lists, the workspace's page and the Ledger are stale. */
export function useRefreshOffers(yardId?: string) {
  const queryClient = useQueryClient();
  return () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: ["admin", "offers"] }),
      queryClient.invalidateQueries({ queryKey: ["admin", "ledger"] }),
      queryClient.invalidateQueries({ queryKey: ["admin", "billing-plans"] }),
      yardId
        ? queryClient.invalidateQueries({ queryKey: ["admin", "workspaces", yardId] })
        : Promise.resolve(),
    ]);
}

export const offerStatusLooks: Record<
  OfferStatus,
  { label: string; tone: "blue" | "green" | "orange" | "grey" }
> = {
  pending: { label: "Waiting", tone: "blue" },
  accepted: { label: "Accepted", tone: "green" },
  declined: { label: "Declined", tone: "orange" },
  withdrawn: { label: "Withdrawn", tone: "grey" },
  expired: { label: "Expired", tone: "grey" },
};

/** "$1,200" for whole dollars, "$999.50" otherwise. */
export function dollars(cents: number): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: cents % 100 === 0 ? 0 : 2,
    maximumFractionDigits: 2,
  }).format(cents / 100);
}

interface Terms {
  monthlyFeeCents: number;
  includedQuotes: number;
  quoteFeeCents: number;
  changeOrderFeeCents: number;
}

/**
 * "$1,200 a month, includes 8 takeoffs, then $150 each; change orders $75":
 * the sentence the workspace reads in its email and in Billing.
 */
export function termsSentence(terms: Terms): string {
  const monthly =
    terms.monthlyFeeCents > 0 ? `${dollars(terms.monthlyFeeCents)} a month` : "No monthly fee";
  const each = terms.quoteFeeCents > 0 ? dollars(terms.quoteFeeCents) : null;
  const takeoffs =
    terms.includedQuotes > 0
      ? `includes ${terms.includedQuotes} ${terms.includedQuotes === 1 ? "takeoff" : "takeoffs"}, then ${each ? `${each} each` : "no charge"}`
      : each
        ? `takeoffs ${each} each`
        : "takeoffs at no charge";
  const changeOrders =
    terms.changeOrderFeeCents > 0
      ? `change orders ${dollars(terms.changeOrderFeeCents)}`
      : "change orders at no charge";
  return `${monthly}, ${takeoffs}; ${changeOrders}`;
}

/** What a month like the last 30 days would cost on these terms. */
export function costOnPlan(usage: { takeoffs: number; changeOrders: number }, terms: Terms) {
  return (
    terms.monthlyFeeCents +
    Math.max(0, usage.takeoffs - terms.includedQuotes) * terms.quoteFeeCents +
    usage.changeOrders * terms.changeOrderFeeCents
  );
}
